import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import initWebSocket, { getSocketCleanup } from 'api/websocket';
import { api } from 'api/api';
import SessionStorageUtils from 'utils/SessionStorageUtils';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';
import { getCurrentSession } from './sessionsSlice';

export const registerUser = createAsyncThunk('auth/registerUser', async (userData, { dispatch, rejectWithValue }) => {
  try {
    const response = await api.post('/auth/register', userData);

    await dispatch(
      loginUser({
        email: userData.email,
        password: userData.password,
      })
    ).unwrap();

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Помилка реєстрації');
  }
});

export const loginUser = createAsyncThunk('auth/loginUser', async (credentials, { dispatch, rejectWithValue }) => {
  try {
    const response = await api.post('/auth/login', credentials);

    const { token, refreshToken } = response.data;

    if (!token || !refreshToken) {
      return rejectWithValue('Отримані некоректні токени');
    }

    TokenUtils.setToken(token);
    TokenUtils.setRefreshToken(refreshToken);

    dispatch(
      addNotification({
        id: Date.now(),
        title: 'Успішний вхід',
        type: 'success',
      })
    );

    initWebSocket(dispatch);

    dispatch(getCurrentUser()).unwrap();
    dispatch(getCurrentSession()).unwrap();

    return { token, refreshToken };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Помилка входу');
  }
});

export const getCurrentUser = createAsyncThunk('auth/getCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();

    const response = await api.get('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати користувача');
  }
});

export const refreshAuthToken = createAsyncThunk('auth/refreshAuthToken', async (_, { dispatch, rejectWithValue }) => {
  const refreshToken = TokenUtils.getRefreshToken();

  if (!refreshToken) {
    dispatch(logoutUser());
    return rejectWithValue('Немає токена оновлення');
  }

  try {
    const response = await api.post('/auth/refresh', { refreshToken });
    const { token: newToken } = response.data;

    if (!newToken) {
      dispatch(logoutUser());
      return rejectWithValue('Отриманий некоректний токен');
    }

    TokenUtils.setToken(newToken);
    return newToken;
  } catch (error) {
    dispatch(logoutUser());
    return rejectWithValue(error.response?.data?.message || 'Не вдалося оновити токен');
  }
});

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { dispatch, rejectWithValue }) => {
  const token = TokenUtils.getToken();

  const cleanup = () => {
    const socketCleanup = getSocketCleanup();
    if (socketCleanup) socketCleanup();

    SessionStorageUtils.clearAll();
    TokenUtils.removeTokens();
  };

  try {
    if (token) {
      await api.post(
        '/auth/logout',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    }

    cleanup();

    dispatch(
      addNotification({
        id: Date.now(),
        title: 'Вихід виконано',
        type: 'success',
      })
    );

    return true;
  } catch (error) {
    cleanup();

    if (error.response?.status !== 401) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Вихід виконано (з помилками)',
          type: 'warning',
        })
      );
    }

    return true;
  }
});

const initialState = {
  user: null,
  error: null,
  isLoading: false,
  token: TokenUtils.getToken(),
  isAuthenticated: !!TokenUtils.getRefreshToken(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    setAuthToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = true;
    },
    resetAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.error = null;
        state.isLoading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.error = action.payload || 'Помилка реєстрації';
        state.isLoading = false;
      })
      .addCase(loginUser.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload || 'Помилка входу';
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.isLoading = false;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати користувача';
        state.isLoading = false;

        if (action.payload?.includes('401') || action.payload?.includes('Unauthorized')) {
          state.isAuthenticated = false;
          state.token = null;
          TokenUtils.removeTokens();
        }
      })
      .addCase(refreshAuthToken.pending, (state) => {
        state.error = null;
      })
      .addCase(refreshAuthToken.fulfilled, (state, action) => {
        state.token = action.payload;
        state.error = null;
      })
      .addCase(refreshAuthToken.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити токен';
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(logoutUser.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
      });
  },
});

export const { clearAuthError, setAuthToken, resetAuthState } = authSlice.actions;
export default authSlice.reducer;
